import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import {
  getCroppedImg,
  formatFileSize,
  analyzeImageAspectRatio,
  detectEdgeColors,
  padImageToSquare
} from '../utils/imageUtils';

/**
 * Image cropper dialog.
 *
 * Props:
 *  - open, image, onClose, onSave, originalFile  (existing)
 *  - cropShape: 'rect' (default) | 'round'       (circular mask for profile pictures)
 *
 * Modos disponibles:
 *  - 'crop':   recortar a cuadrado (1:1) con zoom/rotación. Recomendado.
 *  - 'extend': autoextender el fondo (color de borde detectado o elegido)
 *              para hacer la imagen cuadrada sin recortar contenido.
 *  - 'free':   subir la imagen tal cual, sin recortar ni cuadrar.
 *              No es recomendado porque las cards muestran cuadrado.
 */
const ImageCropper = ({ open, image, onClose, onSave, originalFile, cropShape = 'rect' }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('crop'); // 'crop' | 'extend' | 'free'
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [edgeAnalysis, setEdgeAnalysis] = useState(null);
  const [bgColor, setBgColor] = useState('#ffffff');

  const isRound = cropShape === 'round';

  useEffect(() => {
    if (open && image) {
      analyzeImage();
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setMode('crop');
      setBgColor('#ffffff');
    }
  }, [open, image]);

  const analyzeImage = async () => {
    try {
      const aspectRatio = await analyzeImageAspectRatio(image);
      const edges = await detectEdgeColors(image);
      setImageAnalysis(aspectRatio);
      setEdgeAnalysis(edges);
      if (edges.edgesMatch) {
        setBgColor(edges.hex || '#ffffff');
      }
      if (!aspectRatio.isSquare && edges.edgesMatch) {
        setMode('extend');
      }
    } catch (error) {
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleAutoExtend = async () => {
    setLoading(true);
    try {
      const fileType = originalFile?.type || 'image/jpeg';
      const squaredBlob = await padImageToSquare(image, bgColor, fileType, isRound);
      const fileName = originalFile?.name || 'squared-image.jpg';
      const squaredFile = new File([squaredBlob], fileName, {
        type: fileType,
        lastModified: Date.now(),
      });
      onSave(squaredFile);
      handleClose();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Modo 'free': pasa el File original sin tocar (no cuadra ni recorta).
  const handleFree = () => {
    if (originalFile) {
      onSave(originalFile);
      handleClose();
    }
  };

  const handleSave = async () => {
    if (mode === 'extend') {
      await handleAutoExtend();
      return;
    }
    if (mode === 'free') {
      handleFree();
      return;
    }
    if (!croppedAreaPixels) return;
    setLoading(true);
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels, rotation);
      const fileName = originalFile?.name || 'cropped-image.jpg';
      const croppedFile = new File([croppedImage], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      onSave(croppedFile);
      handleClose();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    onClose();
  };

  const PRESET_COLORS = ['#ffffff', '#f1f5f9', '#e2e8f0', '#0f172a', '#059669', '#2563eb', '#7c3aed', '#e11d48'];

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50"
      onClick={handleClose}
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90dvh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-outline-variant/30">
          <h2 className="text-base font-semibold text-on-surface">
            {isRound ? t('Editar foto de perfil') : t('Optimizar imagen')}
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-surface-container transition-colors"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-on-surface-variant">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Analysis info alert — la ocultamos en modo round (foto de perfil)
              porque ahí el círculo ya deja claro qué se va a ver. */}
          {!isRound && imageAnalysis && !imageAnalysis.isSquare && (
            <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
              edgeAnalysis?.edgesMatch
                ? 'bg-blue-50 text-blue-800 border border-blue-200'
                : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
            }`}>
              <span className="material-symbols-outlined text-[18px] mt-0.5 shrink-0">
                {edgeAnalysis?.edgesMatch ? 'info' : 'warning'}
              </span>
              <div>
                {edgeAnalysis?.edgesMatch ? (
                  <>
                    <p className="font-bold">{t('Detectamos colores de borde uniformes.')}</p>
                    <p>{t('Podés autoextender el fondo para hacerla cuadrada, o recortar manualmente.')}</p>
                  </>
                ) : (
                  <>
                    <p className="font-bold">
                      {imageAnalysis.orientation === 'portrait'
                        ? t('La imagen es vertical.')
                        : imageAnalysis.orientation === 'landscape'
                          ? t('La imagen es horizontal.')
                          : t('La imagen no es cuadrada.')}
                    </p>
                    <p>
                      {isRound
                        ? t('Recortá el área que va a aparecer dentro del círculo.')
                        : t('Recortá a un área cuadrada para que se vea mejor en las cards de producto.')}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Mode toggle — en modo round (foto de perfil) sólo hay
              "Recortar", así que ocultamos el toggle entero. */}
          {!isRound && (
          <div className="flex justify-center">
            <div className="inline-flex rounded-lg border border-outline-variant overflow-hidden text-sm flex-wrap">
              <button
                onClick={() => setMode('crop')}
                className={`flex items-center gap-1.5 px-4 py-2 font-medium transition-colors ${
                  mode === 'crop'
                    ? 'bg-primary text-on-primary'
                    : 'bg-transparent text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <span className="material-symbols-outlined text-[16px]">crop</span>
                {t('Recortar a cuadrado')}
              </button>
              <button
                onClick={() => setMode('extend')}
                className={`flex items-center gap-1.5 px-4 py-2 font-medium transition-colors ${
                  mode === 'extend'
                    ? 'bg-primary text-on-primary'
                    : 'bg-transparent text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <span className="material-symbols-outlined text-[16px]">crop_free</span>
                {t('Autoextender')}
              </button>
              <button
                onClick={() => setMode('free')}
                className={`flex items-center gap-1.5 px-4 py-2 font-medium transition-colors ${
                  mode === 'free'
                    ? 'bg-primary text-on-primary'
                    : 'bg-transparent text-on-surface-variant hover:bg-surface-container'
                }`}
                title={t('No recomendado: la imagen no se va a ver cuadrada en las cards')}
              >
                <span className="material-symbols-outlined text-[16px]">aspect_ratio</span>
                {t('Libre')}
              </button>
            </div>
          </div>
          )}

          {mode === 'crop' && (
            <>
              {/* Crop area */}
              <div
                className="relative w-full rounded-lg overflow-hidden"
                style={{ height: 350, backgroundColor: '#f8fafc' }}
              >
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  minZoom={1}
                  maxZoom={4}
                  zoomSpeed={0.5}
                  cropShape={isRound ? 'round' : 'rect'}
                  showGrid={!isRound}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: { background: '#f8fafc' },
                    cropAreaStyle: {
                      border: isRound ? '2px solid rgba(255,255,255,0.6)' : '2px solid #1976d2',
                    },
                  }}
                />
              </div>

              {/* Controls */}
              <div className="flex gap-3 items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-on-surface-variant font-medium">{t('Zoom')}</span>
                    <span className="text-xs text-on-surface tabular-nums font-bold">
                      {Math.round(zoom * 100)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setZoom(z => Math.max(1, Math.round((z - 0.1) * 100) / 100))}
                      className="w-8 h-8 shrink-0 rounded-full border border-outline-variant hover:bg-surface-container active:bg-surface-container-high transition-colors flex items-center justify-center"
                      aria-label={t('Reducir zoom')}
                      disabled={zoom <= 1}
                    >
                      <span className="material-symbols-outlined text-[18px] text-on-surface-variant">remove</span>
                    </button>
                    <input
                      type="range"
                      min={1}
                      max={4}
                      step={0.01}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="cm-cropper-zoom flex-1 min-w-0"
                      aria-label={t('Zoom')}
                    />
                    <button
                      type="button"
                      onClick={() => setZoom(z => Math.min(4, Math.round((z + 0.1) * 100) / 100))}
                      className="w-8 h-8 shrink-0 rounded-full border border-outline-variant hover:bg-surface-container active:bg-surface-container-high transition-colors flex items-center justify-center"
                      aria-label={t('Aumentar zoom')}
                      disabled={zoom >= 4}
                    >
                      <span className="material-symbols-outlined text-[18px] text-on-surface-variant">add</span>
                    </button>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 self-end">
                  <button
                    onClick={() => setRotation(r => r - 90)}
                    className="w-9 h-9 rounded-lg border border-outline-variant hover:bg-surface-container transition-colors flex items-center justify-center"
                    aria-label={t('Rotar a la izquierda')}
                  >
                    <span className="material-symbols-outlined text-[18px] text-on-surface-variant">rotate_left</span>
                  </button>
                  <button
                    onClick={() => setRotation(r => r + 90)}
                    className="w-9 h-9 rounded-lg border border-outline-variant hover:bg-surface-container transition-colors flex items-center justify-center"
                    aria-label={t('Rotar a la derecha')}
                  >
                    <span className="material-symbols-outlined text-[18px] text-on-surface-variant">rotate_right</span>
                  </button>
                </div>
              </div>
              <style>{`
                .cm-cropper-zoom {
                  -webkit-appearance: none;
                  appearance: none;
                  height: 6px;
                  border-radius: 9999px;
                  background: linear-gradient(
                    to right,
                    var(--md-sys-color-primary, #1976d2) 0%,
                    var(--md-sys-color-primary, #1976d2) ${((zoom - 1) / 3) * 100}%,
                    rgba(0,0,0,0.10) ${((zoom - 1) / 3) * 100}%,
                    rgba(0,0,0,0.10) 100%
                  );
                  outline: none;
                  cursor: pointer;
                }
                .cm-cropper-zoom::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  background: var(--md-sys-color-primary, #1976d2);
                  border: 2px solid #fff;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
                  cursor: grab;
                  transition: transform 120ms ease;
                }
                .cm-cropper-zoom::-webkit-slider-thumb:active {
                  transform: scale(1.15);
                  cursor: grabbing;
                }
                .cm-cropper-zoom::-moz-range-thumb {
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  background: var(--md-sys-color-primary, #1976d2);
                  border: 2px solid #fff;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.25);
                  cursor: grab;
                  transition: transform 120ms ease;
                }
                .cm-cropper-zoom::-moz-range-thumb:active {
                  transform: scale(1.15);
                  cursor: grabbing;
                }
              `}</style>
            </>
          )}

          {mode === 'extend' && (
            <>
              <div
                className="relative w-full overflow-hidden flex items-center justify-center border-2 border-dashed border-outline-variant"
                style={{
                  height: 350,
                  backgroundColor: bgColor,
                  borderRadius: isRound ? '50%' : '0.5rem',
                  ...(isRound ? { width: 350, margin: '0 auto' } : {}),
                }}
              >
                <img
                  src={image}
                  alt={t('Vista previa')}
                  style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain', borderRadius: '0.25rem' }}
                />
              </div>

              {/* Background color picker */}
              <div>
                <p className="text-xs text-on-surface-variant mb-2">{t('Color de fondo')}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setBgColor(color)}
                      className="w-7 h-7 rounded-full transition-transform hover:scale-110 shrink-0"
                      style={{
                        backgroundColor: color,
                        border: bgColor === color ? '2.5px solid #1976d2' : '2px solid #e2e8f0',
                        boxShadow: bgColor === color ? '0 0 0 2px rgba(25,118,210,0.3)' : 'none',
                      }}
                      aria-label={t('Color de fondo {{color}}', { color })}
                    />
                  ))}
                  {edgeAnalysis?.hex && !PRESET_COLORS.includes(edgeAnalysis.hex) && (
                    <button
                      onClick={() => setBgColor(edgeAnalysis.hex)}
                      title={t('Detectado: {{hex}}', { hex: edgeAnalysis.hex })}
                      className="w-7 h-7 rounded-full transition-transform hover:scale-110 shrink-0 relative"
                      style={{
                        backgroundColor: edgeAnalysis.hex,
                        border: bgColor === edgeAnalysis.hex ? '2.5px solid #1976d2' : '2px solid #e2e8f0',
                        boxShadow: bgColor === edgeAnalysis.hex ? '0 0 0 2px rgba(25,118,210,0.3)' : 'none',
                      }}
                    >
                      <span className="absolute -top-1.5 -right-1 text-[10px] text-blue-600">✦</span>
                    </button>
                  )}
                  <div className="relative w-7 h-7">
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="absolute inset-0 w-full h-full cursor-pointer opacity-0 rounded-full"
                    />
                    <div
                      className="w-7 h-7 rounded-full border-2 border-outline-variant pointer-events-none"
                      style={{ background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {mode === 'free' && (
            <>
              <div className="rounded-lg p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 text-sm flex items-start gap-2">
                <span className="material-symbols-outlined text-[18px] mt-0.5 shrink-0">warning</span>
                <div>
                  <p className="font-bold">{t('Modo libre — no recomendado')}</p>
                  <p>{t('Las cards de producto se muestran cuadradas. Si subís la imagen sin recortar, se va a recortar automáticamente al mostrarse y podés perder partes importantes.')}</p>
                </div>
              </div>
              <div
                className="relative w-full overflow-hidden flex items-center justify-center border border-outline-variant rounded-lg bg-[#f8fafc]"
                style={{ height: 350 }}
              >
                <img
                  src={image}
                  alt={t('Vista previa')}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </div>
            </>
          )}

          {/* File size info */}
          {originalFile && (
            <p className="text-xs text-on-surface-variant">
              {t('Tamaño original')}: {formatFileSize(originalFile.size)}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="shrink-0 flex justify-end gap-3 px-4 py-3 border-t border-outline-variant/30 bg-surface">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg text-on-surface-variant font-medium text-sm hover:bg-surface-container transition-colors border border-outline-variant"
          >
            {t('Cancelar')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading || (mode === 'crop' && !croppedAreaPixels)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-on-primary font-medium text-sm disabled:opacity-60 transition-colors"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="material-symbols-outlined text-[16px]">
                {mode === 'extend' ? 'crop_free' : mode === 'free' ? 'aspect_ratio' : 'crop'}
              </span>
            )}
            {loading
              ? t('Procesando...')
              : mode === 'extend'
                ? t('Aplicar')
                : mode === 'free'
                  ? t('Usar tal cual')
                  : t('Recortar y guardar')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
